import { SEARCH_PAGE_CATEGORY, SEARCHABLE_PAGE_ID, SEARCHABLE_PAGE_TYPE, SearchablePageMetadata } from './types'
import {
  SCHEDULE_COMPLEX_CARE_CALL_LINK,
  SCHEDULE_DBT_CALL_LINK,
} from '../common/constants/complexCareMicrositeConstants'
import { BRANCH_APP_LINK } from '../common/constants/constants'
import { FAQ_ROUTE } from '../common/constants/faqConstants'
import {
  HEALTH_CHALLENGES_BASE_ROUTE,
  NEURODIVERSITY_BASE_ROUTE,
  PAGE_ROUTES,
  WELLNESS_TOOLKIT_BASE_ROUTE,
} from '../common/constants/routingConstants'
import {
  AUD_PARAMS,
  COACHING_PARAMS,
  COUPLES_PARAMS,
  FAMILY_PARAMS,
  MEDS_PARAMS,
  PARENTING_PARAMS,
} from '../common/constants/speculativeConstant'

/**
 * P0 Searchable pages for Universal Search Phase 1.
 * Ordered by priority within each category.
 *
 * ELIGIBILITY CONDITIONS:
 * Each page may have conditions that determine visibility. These are documented
 * inline with each entry. Common condition types:
 * - Feature flags (LaunchDarkly)
 * - Customer properties (customerPropertiesMap)
 * - User properties (age, international status, etc.)
 * - Program eligibility (customerSupportedPrograms)
 */
export const SEARCHABLE_PAGES: SearchablePageMetadata[] = [
  // ============ TRIAGE ============
  {
    id: SEARCHABLE_PAGE_ID.ADULT_TRIAGE,
    displayName: 'Search for care',
    keywords: [
      'therapy',
      'psychologist',
      'counselling',
      'coaching',
      'life coach',
      'triage',
      'provider search',
      'new provider',
      'psychotherapy',
    ], // TODO: include conditions from triage service (primary need and word cloud terms)
    category: SEARCH_PAGE_CATEGORY.TRIAGE,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.ONBOARD.ABOUT,
  },
  {
    id: SEARCHABLE_PAGE_ID.CHILD_TRIAGE,
    displayName: 'Child & teen care',
    keywords: [
      'child therapy',
      'toddler therapy',
      'adolescent therapy',
      'teen therapy',
      'child counseling',
      'toddler counseling',
      'adolescent counseling',
      'teen counseling',
      'child psychologist',
      'child psychotherapy',
    ], // TODO: include conditions from triage service (primary need and word cloud terms)
    category: SEARCH_PAGE_CATEGORY.TRIAGE,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.ONBOARD.ABOUT_CHILD,
  },
  {
    id: SEARCHABLE_PAGE_ID.FORKED_TRIAGE,
    displayName: 'Care options',
    keywords: ['care options', 'programs'],
    category: SEARCH_PAGE_CATEGORY.TRIAGE,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE,
  },

  // ============ PROGRAMS (DPLs) ============
  // All DPLs use strictMatchOnly to prevent vague searches from surfacing sensitive programs
  {
    id: SEARCHABLE_PAGE_ID.COACHING,
    displayName: 'Mental Health Coaching',
    keywords: ['coaching'],
    category: SEARCH_PAGE_CATEGORY.PROGRAM,
    type: SEARCHABLE_PAGE_TYPE.DIRECT_PROGRAM_LINK,
    directLinkParams: COACHING_PARAMS,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_PAGE_ID.PARENTING_COACHING,
    displayName: 'Coaching for Parents',
    keywords: ['coaching', 'parent', 'tantrum'],
    category: SEARCH_PAGE_CATEGORY.PROGRAM,
    type: SEARCHABLE_PAGE_TYPE.DIRECT_PROGRAM_LINK,
    directLinkParams: PARENTING_PARAMS,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_PAGE_ID.COUPLES_THERAPY,
    displayName: 'Couples therapy',
    keywords: ['couples', 'relationship'],
    category: SEARCH_PAGE_CATEGORY.PROGRAM,
    type: SEARCHABLE_PAGE_TYPE.DIRECT_PROGRAM_LINK,
    directLinkParams: COUPLES_PARAMS,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_PAGE_ID.FAMILY_THERAPY,
    displayName: 'Family therapy',
    keywords: ['family', 'relationship', 'caregiver'],
    category: SEARCH_PAGE_CATEGORY.PROGRAM,
    type: SEARCHABLE_PAGE_TYPE.DIRECT_PROGRAM_LINK,
    directLinkParams: FAMILY_PARAMS,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_PAGE_ID.MEDICATION_CONSULT,
    displayName: 'Medication consultation',
    keywords: ['meds', 'medication', 'prescription', 'prescriber', 'drug'],
    category: SEARCH_PAGE_CATEGORY.PROGRAM,
    type: SEARCHABLE_PAGE_TYPE.DIRECT_PROGRAM_LINK,
    directLinkParams: MEDS_PARAMS,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_PAGE_ID.RENEW_AUD,
    displayName: 'Alcohol & substance support',
    keywords: ['substance abuse', 'alcohol', 'SUD', 'drug'],
    category: SEARCH_PAGE_CATEGORY.PROGRAM,
    type: SEARCHABLE_PAGE_TYPE.DIRECT_PROGRAM_LINK,
    directLinkParams: AUD_PARAMS,
    strictMatchOnly: true,
  },

  // ============ COMPLEX CARE / EXTERNAL ============
  {
    id: SEARCHABLE_PAGE_ID.COMPLEX_CARE,
    displayName: 'Complex Care',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.CARE,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.COMPLEX_CARE,
  },
  {
    id: SEARCHABLE_PAGE_ID.DBT,
    displayName: 'DBT (support for self-harm)',
    keywords: ['DBT', 'Dialectical Behavior Therapy', 'BPD', 'Borderline Personality Disorder'],
    category: SEARCH_PAGE_CATEGORY.EXTERNAL,
    type: SEARCHABLE_PAGE_TYPE.EXTERNAL_URL,
    url: SCHEDULE_DBT_CALL_LINK,
  },
  {
    id: SEARCHABLE_PAGE_ID.IOP,
    displayName: 'Intensive Outpatient Program (IOP)',
    keywords: ['IOP', 'facility', 'intensive outpatient program', 'intensive outpatient treatment'],
    category: SEARCH_PAGE_CATEGORY.EXTERNAL,
    type: SEARCHABLE_PAGE_TYPE.EXTERNAL_URL,
    url: SCHEDULE_COMPLEX_CARE_CALL_LINK,
  },
  {
    id: SEARCHABLE_PAGE_ID.PHP,
    displayName: 'Partial Hospitalization Program (PHP)',
    keywords: ['PHP', 'partial', 'partial hospitalization'],
    category: SEARCH_PAGE_CATEGORY.EXTERNAL,
    type: SEARCHABLE_PAGE_TYPE.EXTERNAL_URL,
    url: SCHEDULE_COMPLEX_CARE_CALL_LINK,
  },
  {
    id: SEARCHABLE_PAGE_ID.RESIDENTIAL,
    displayName: 'Residential Treatment',
    keywords: ['residential', 'facility'],
    category: SEARCH_PAGE_CATEGORY.EXTERNAL,
    type: SEARCHABLE_PAGE_TYPE.EXTERNAL_URL,
    url: SCHEDULE_COMPLEX_CARE_CALL_LINK,
  },
  {
    id: SEARCHABLE_PAGE_ID.INPATIENT,
    displayName: 'Inpatient Hospitalization',
    keywords: ['hospital', 'hospitalization', 'inpatient hospital', 'inpatient program'],
    category: SEARCH_PAGE_CATEGORY.EXTERNAL,
    type: SEARCHABLE_PAGE_TYPE.EXTERNAL_URL,
    url: SCHEDULE_COMPLEX_CARE_CALL_LINK,
  },

  // ============ TOOLKITS ============
  // All toolkits use WellnessToolkitAvailableConfig system with conditions including:
  // - Feature flags (per toolkit)
  // - Not teen, not international, English display language
  // - Provider supply available, not in active episode, not T3 recommendation
  // See useIsWellnessToolkitAvailable hook and getWellnessToolkitAvailableConfig for full logic
  {
    id: SEARCHABLE_PAGE_ID.TOOLKIT_RELATIONSHIPS,
    displayName: 'Relationships',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.TOOLKIT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/relationships`,
  },
  {
    id: SEARCHABLE_PAGE_ID.TOOLKIT_WORK,
    displayName: 'Work',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.TOOLKIT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/work`,
  },
  {
    id: SEARCHABLE_PAGE_ID.TOOLKIT_FAMILY,
    displayName: 'Family Hub',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.TOOLKIT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.FAMILY_HUB,
  },
  {
    id: SEARCHABLE_PAGE_ID.TOOLKIT_NEURODIVERSITY,
    displayName: 'Neurodiversity',
    keywords: ['neurodivergent', 'neurodiversity', 'neuropsych consult', 'neuro', 'autism', 'ASD', 'ADHD', 'autistic'],
    category: SEARCH_PAGE_CATEGORY.TOOLKIT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: NEURODIVERSITY_BASE_ROUTE,
  },
  {
    id: SEARCHABLE_PAGE_ID.TOOLKIT_HEALTH_CHALLENGES,
    displayName: 'Health challenges',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.TOOLKIT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: HEALTH_CHALLENGES_BASE_ROUTE,
  },
  {
    id: SEARCHABLE_PAGE_ID.TOOLKIT_PARENTING,
    displayName: 'Parenting',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.TOOLKIT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/parent`,
  },
  {
    id: SEARCHABLE_PAGE_ID.TOOLKIT_VALUES,
    displayName: 'Values',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.TOOLKIT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/purpose`,
  },
  {
    id: SEARCHABLE_PAGE_ID.TOOLKIT_TRAUMA,
    displayName: 'Trauma support',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.TOOLKIT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/trauma`,
  },

  // ============ CARE TAB ============
  {
    id: SEARCHABLE_PAGE_ID.CARE_TAB,
    displayName: 'Care',
    keywords: ['lessons', 'activities', 'appointment', 'session'],
    category: SEARCH_PAGE_CATEGORY.CARE,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.ASSIGNMENTS_NEW, //replace this
  },
  {
    id: SEARCHABLE_PAGE_ID.SESSIONS,
    displayName: 'Sessions',
    keywords: ['sessions', 'appointment'],
    category: SEARCH_PAGE_CATEGORY.CARE,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.SESSIONS,
  },

  // ============ LIBRARY ============
  {
    id: SEARCHABLE_PAGE_ID.LIBRARY,
    displayName: 'Library',
    keywords: ['self-care', 'resources', 'library', 'content', 'articles'],
    category: SEARCH_PAGE_CATEGORY.LIBRARY,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.ESSENTIALS,
  },
  {
    id: SEARCHABLE_PAGE_ID.EVENTS,
    displayName: 'Events',
    keywords: ['events', 'live events'],
    category: SEARCH_PAGE_CATEGORY.LIBRARY,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.ESSENTIALS_EVENTS,
  },
  {
    id: SEARCHABLE_PAGE_ID.MEDITATION,
    displayName: 'Meditation',
    keywords: ['meditate', 'meditation', 'mindfulness', 'sleep'],
    category: SEARCH_PAGE_CATEGORY.LIBRARY,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.ESSENTIALS,
  },

  // ============ BENEFITS ============
  {
    id: SEARCHABLE_PAGE_ID.BENEFITS,
    displayName: 'My benefits',
    keywords: ['care options', 'offerings', 'benefits', 'services'],
    category: SEARCH_PAGE_CATEGORY.CARE,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.SEARCH_CARE.BENEFITS,
  },
  {
    id: SEARCHABLE_PAGE_ID.WELLNESS_CHECK_IN,
    displayName: 'Wellness check-in',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.CARE,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.WELLNESS_CHECK_IN.DEFAULT,
  },

  // ============ ACCOUNT ============
  {
    id: SEARCHABLE_PAGE_ID.ACCOUNT,
    displayName: 'Account',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.DEFAULT,
  },
  {
    id: SEARCHABLE_PAGE_ID.SETTINGS,
    displayName: 'Settings',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.SETTINGS,
  },
  {
    id: SEARCHABLE_PAGE_ID.SESSION_HISTORY,
    displayName: 'Session history',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.SESSION_HISTORY,
  },
  {
    id: SEARCHABLE_PAGE_ID.COVERAGE_INFO,
    displayName: 'Coverage information',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.ELIGIBILITY,
  },
  {
    id: SEARCHABLE_PAGE_ID.HEALTH_PLAN_PAYMENT,
    displayName: 'Health plan and payment',
    keywords: ['credit card'],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.HEALTH_PLAN_AND_PAYMENTS,
  },
  {
    id: SEARCHABLE_PAGE_ID.PRIVACY_LEGAL,
    displayName: 'Privacy and Legal',
    keywords: ['consent'],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.PRIVACY_AND_LEGAL,
  },
  {
    id: SEARCHABLE_PAGE_ID.CLINICAL_DATA_SHARING,
    displayName: 'Share your information',
    keywords: ['health record', 'record request'],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.CLINICAL_DATA_SHARING,
  },
  {
    id: SEARCHABLE_PAGE_ID.ADD_DEPENDENT,
    displayName: 'Add a dependent',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.ADD_FAMILY_MEMBER,
  },
  {
    id: SEARCHABLE_PAGE_ID.CHANGE_EMAIL,
    displayName: 'Change email',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.CHANGE_EMAIL,
  },
  {
    id: SEARCHABLE_PAGE_ID.MFA,
    displayName: 'Two-factor authentication',
    keywords: ['MFA', '2FA'],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.ENROLL_MFA,
  },
  {
    id: SEARCHABLE_PAGE_ID.PHONE_NUMBER,
    displayName: 'Phone number',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.UPDATE_PHONE_NUMBER,
  },
  {
    id: SEARCHABLE_PAGE_ID.LOCATION_TIMEZONE,
    displayName: 'Location & timezone',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.UPDATE_LOCATION,
  },
  {
    id: SEARCHABLE_PAGE_ID.DISPLAY_LANGUAGE,
    displayName: 'Display language',
    keywords: ['language'],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.UPDATE_LANGUAGE,
  },
  {
    id: SEARCHABLE_PAGE_ID.NOTIFICATIONS,
    displayName: 'Notifications',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.UPDATE_NOTIFICATION_SETTINGS,
  },
  {
    id: SEARCHABLE_PAGE_ID.TRANSFER_ACCOUNT,
    displayName: 'Transfer your Lyra account',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.TRANSFER_ACCOUNT.FIND_EMPLOYER,
  },
  {
    id: SEARCHABLE_PAGE_ID.ADD_HEALTH_PLAN,
    displayName: 'Add Health Plan',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.HPI_COVERAGE_CHECK.PROFILE.HEALTH_PLAN_FORM,
  },
  {
    id: SEARCHABLE_PAGE_ID.ADD_PAYMENT_METHOD,
    displayName: 'Add payment method',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.HPI_COVERAGE_CHECK.PROFILE.PAYMENT_INFO_FORM,
  },
  {
    id: SEARCHABLE_PAGE_ID.LATE_CANCELLATION_PAYMENT,
    displayName: 'Late cancellation and no-show payment',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.HPI_COVERAGE_CHECK.PROFILE.INFRACTION_PAYMENT_INFO_FORM,
  },
  {
    id: SEARCHABLE_PAGE_ID.FORGOT_PASSWORD,
    displayName: 'Password',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.ACCOUNT,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.RESET_PASSWORD,
  },

  // ============ EXTERNAL LINKS ============
  {
    id: SEARCHABLE_PAGE_ID.LYRA_APP,
    displayName: 'Lyra Health app',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.EXTERNAL,
    type: SEARCHABLE_PAGE_TYPE.EXTERNAL_URL,
    url: BRANCH_APP_LINK,
  },

  // ============ HELP ============
  {
    id: SEARCHABLE_PAGE_ID.FAQ,
    displayName: 'Frequently Asked Questions',
    keywords: [],
    category: SEARCH_PAGE_CATEGORY.HELP,
    type: SEARCHABLE_PAGE_TYPE.STANDARD,
    route: FAQ_ROUTE,
  },
]

/**
 * Pages requiring strict matching (threshold 0.1).
 * Used for sensitive programs where vague searches should not surface the item.
 */
export const STRICT_MATCH_PAGES = SEARCHABLE_PAGES.filter((page) => page.strictMatchOnly)

/**
 * Pages using standard fuzzy matching (threshold 0.4).
 */
export const STANDARD_PAGES = SEARCHABLE_PAGES.filter((page) => !page.strictMatchOnly)
